import {
    ContainerAlignment,
    ContainerLayout,
    ContainerPadding,
    ContainerWidth,
} from "@glide/common-core/dist/js/description";
import type { ComponentDescription, PropertyDescription } from "@glide/app-description";
import {
    makeStringProperty,
    getSourceColumnProperty,
    getStringProperty,
    getEnumProperty,
} from "@glide/app-description";
import { ArrayPropertyStyle, PropertySection, resolveSourceColumn } from "@glide/function-utils";
import { WireComponentKind, HeroImageEffect, UIBackgroundStyle, type WireComponent } from "@glide/wire";

import type { WireButtonComponent } from "./fluent-components";
import type { DescriptionOfComponent } from "./fluent-components-spec";
import {
    properties,
    type WirePropertiesOfComponent,
    ContainerKind,
    FluentComponent,
    containerPropertiesWithCSS,
    emptyPropertiesSpec,
    propertiesWithCSS,
} from "./fluent-components-spec";

import isString from "lodash/isString";
import { isEmptyOrUndefined } from "@glide/support";

function container<TKind extends WireComponentKind>(kind: TKind) {
    return new FluentComponent<
        unknown,
        unknown,
        { readonly kind: TKind; readonly components: readonly (WireComponent | null)[] },
        {}
    >({
        kind,
        containerKind: ContainerKind.Regular,
        properties: emptyPropertiesSpec,
        formProperties: emptyPropertiesSpec,
        includeRegularComponent: true,
        internalState: {},
    });
}

function formContainer<TKind extends WireComponentKind>(kind: TKind) {
    return new FluentComponent<
        unknown,
        unknown,
        {
            readonly kind: TKind;
            readonly components: readonly (WireComponent | null)[];
            readonly specialComponents: readonly (WireButtonComponent | null)[];
        },
        {}
    >({
        kind,
        containerKind: ContainerKind.Form,
        properties: emptyPropertiesSpec,
        formProperties: emptyPropertiesSpec,
        includeRegularComponent: true,
        internalState: {},
    });
}

export const Container = container(WireComponentKind.Container)
    .withDisplayName("Container")
    .withIcon("mt-component-container")
    .withDescription("Group multiple components together and edit the background")
    .withProperties(
        containerPropertiesWithCSS
            .withEnum(
                "containerStyle",
                [
                    { value: UIBackgroundStyle.None, label: "None" },
                    { value: UIBackgroundStyle.Card, label: "Card" },
                    { value: UIBackgroundStyle.Accent, label: "Accent" },
                    { value: UIBackgroundStyle.Dark, label: "Dark" },
                    { value: UIBackgroundStyle.Highlight, label: "Highlight" },
                    { value: UIBackgroundStyle.Image, label: "Image" },
                ],
                {
                    displayName: "Background",
                    propertySection: PropertySection.Design,
                }
            )
            .withEnum(
                "padding",
                [
                    { value: ContainerPadding.Medium, label: "Normal", icon: "mt-padding-sm" },
                    { value: ContainerPadding.Large, label: "Large", icon: "mt-padding-md" },
                    { value: ContainerPadding.XLarge, label: "XLarge", icon: "mt-padding-lg" },
                ],
                {
                    propertySection: PropertySection.Design,
                    enumVisual: "small-images",
                }
            )
            .withEnum(
                "width",
                [
                    { value: ContainerWidth.Normal, label: "Large", icon: "mt-container-width-md" },
                    { value: ContainerWidth.Small, label: "Medium", icon: "mt-container-width-sm" },
                    { value: ContainerWidth.XSmall, label: "Small", icon: "mt-container-width-xs" },
                ],
                {
                    propertySection: PropertySection.Design,
                    enumVisual: "small-images",
                }
            )
            .withEnum(
                "alignment",
                [
                    { value: ContainerAlignment.center, label: "center", icon: "mt-container-align-center" },
                    { value: ContainerAlignment.top, label: "top", icon: "mt-container-align-top" },
                ],
                {
                    propertySection: PropertySection.Design,
                    enumVisual: "small-images",
                    when: d => {
                        const layout = getEnumProperty(d.layout);
                        return layout !== undefined && layout !== ContainerLayout.Full;
                    },
                }
            )

            .withImage("backgroundImage", {
                propertySection: PropertySection.Design,
                when: d => getEnumProperty(d.containerStyle) === UIBackgroundStyle.Image,
                subcomponent: {
                    name: "Background",
                    important: true,
                },
            })
            .withEnum("backgroundEffect", [HeroImageEffect.None, HeroImageEffect.Darken, HeroImageEffect.Lighten], {
                displayName: "Background Effect",
                propertySection: PropertySection.Design,
                when: d => getEnumProperty(d.containerStyle) === UIBackgroundStyle.Image,
                subcomponent: {
                    name: "background",
                    important: true,
                },
            })
            .withSwitch("withBlur", false, {
                propertySection: PropertySection.Design,
                when: d => getEnumProperty(d.containerStyle) === UIBackgroundStyle.Image,
                subcomponent: {
                    name: "Background",
                    important: true,
                },
            })
    );
export type WireContainerComponent = WirePropertiesOfComponent<typeof Container>;
export type WireContainerComponentDescription = DescriptionOfComponent<typeof Container> & {
    components: readonly ComponentDescription[];
};

export const FormContainer = formContainer(WireComponentKind.FormContainer)
    .withDisplayName("Form Container")
    .withDescription("Attach a group of form components to any source in your project.")
    .withIcon("mt-component-container-form")
    .withProperties(
        propertiesWithCSS.withEnum(
            "containerStyle",
            [
                { value: UIBackgroundStyle.None, label: "None" },
                { value: UIBackgroundStyle.Card, label: "Card" },
                { value: UIBackgroundStyle.Accent, label: "Accent" },
                { value: UIBackgroundStyle.Dark, label: "Dark" },
                { value: UIBackgroundStyle.Highlight, label: "Highlight" },
            ],
            {
                displayName: "Background",
                propertySection: PropertySection.Design,
            }
        )
    );
export type WireFormContainerComponent = WirePropertiesOfComponent<typeof FormContainer>;

export function makeTabsNavigationContainerProperties() {
    return propertiesWithCSS
        .withEnum<"style", "button" | "line">(
            "style",
            [
                { value: "line", label: "Line" },
                { value: "button", label: "Button" },
            ],
            {
                defaultValue: "line",
                enumVisual: "text",
                propertySection: PropertySection.Design,
            }
        )
        .withArray(
            "paths",
            properties
                .withRoot()
                .withText("path", {
                    displayName: "Name",
                    emptyByDefault: true,
                })
                .withText("displayAs", {
                    displayName: "Display as",
                    emptyByDefault: true,
                })
                .withIcon("icon")
                .withText("badge", {
                    emptyByDefault: true,
                })
                .withSwitch("visibilityCondition", true, {
                    withCondition: true,
                    hideToggle: true,
                    conditionPrompt: "Set conditions for when the tab should be visible based on user profile data.",
                    specPrefix: "Show tab when",
                    allowSpecialValues: true,
                    propertySection: {
                        // Using custom Visibility one because otherwise it goes under the Option tab
                        name: "Visibility",
                        order: 1,
                    },
                }),
            {
                displayName: "Tabs",
                popUpHeaderDisplay: "Edit tab",
                style: ArrayPropertyStyle.Popup,
                propertySection: {
                    name: "Tabs",
                    order: 1,
                },
                getHeaderDisplay: (desc, _rootDesc, tables, adc) => {
                    function getPropertyValue(property: PropertyDescription | undefined) {
                        if (property === undefined) {
                            return "-";
                        }

                        const pathDisplayValue = getStringProperty(property ?? "");

                        if (!isEmptyOrUndefined(pathDisplayValue)) {
                            return pathDisplayValue;
                        }

                        const sc = getSourceColumnProperty(property);
                        if (sc === undefined) return "-";

                        const tac = resolveSourceColumn(adc, sc, tables?.input, undefined, undefined)?.tableAndColumn;
                        if (tac === undefined) return "-";

                        return tac.column.displayName ?? tac.column.name;
                    }

                    const displayAsValue = getPropertyValue(desc.displayAs);

                    if (displayAsValue === "-") {
                        return getPropertyValue(desc.path);
                    }

                    return displayAsValue;
                },
                dataSelectors: ["Display as", "Name"],
                defaultValue: [
                    {
                        path: makeStringProperty("Tab"),
                    },
                ],
            }
        );
}

export const TabsNavigationBarContainer = container(WireComponentKind.TabsNavigationBarContainer)
    .withExperimentFlag("tabsComponent")
    .withBeta(true)
    .withDisplayName("Tabs Container")
    .withDescription("Create a navigation tab container.")
    .withIcon("mt-component-tabs-container")
    .withInternalState("match", {
        defaultValue: "",
        persist: true,
        validator: isString,
    })
    .withProperties(
        makeTabsNavigationContainerProperties().withEnum(
            "containerStyle",
            [
                { value: UIBackgroundStyle.None, label: "None" },
                { value: UIBackgroundStyle.Card, label: "Card" },
                { value: UIBackgroundStyle.Highlight, label: "Highlight" },
            ],
            {
                displayName: "Background",
                propertySection: PropertySection.Design,
            }
        )
    );

export type WireTabNavigationBarContainer = WirePropertiesOfComponent<typeof TabsNavigationBarContainer>;
